import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import LiveDictationSession from "../../../liveDictationSession";

const RECORDING_CLASSES = ["!text-red-600", "!border-red-400", "!bg-red-50", "animate-pulse"];
// UX limit, not a billing boundary: with browser-direct streaming nothing server-side can
// terminate an established vendor connection. Cost control = short presign validity,
// issuance throttles, and AWS quotas/budget alarms (accepted trusted-client risk).
const MAX_RECORDING_MS = 60_000;
const MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

// The composer and the Home box can be on the page at once; only one may record at a time.
let activeController = null;

export default class extends Controller {
  static targets = ["button", "input", "notice", "micIcon", "busyIcon"];
  static values = { url: String, streamUrl: String };

  #starting = null;
  #upgrade = null;
  #recorder = null;
  #chunks = [];
  #discarded = false;
  #uploadToken = null;
  #maxTimer = null;
  #noticeTimeout = null;
  #form = null;
  #live = null;
  #baseValue = "";
  // The submitted value is already serialized; a pending recording or transcription
  // must not write into the cleared composer afterwards.
  #handleSubmit = () => this.#cancel();

  // Live results rewrite the dictated region, so typing mid-stream would be clobbered
  // by the next event: the keyboard wins. That includes a pending upgrade — letting the
  // shadow carry on would append its transcript later, after the user chose to type.
  // Plain batch mode appends once and doesn't need this.
  #handleInput = (event) => {
    if (!event.isTrusted) return;
    if (this.#upgrade) {
      this.#cancel();
    } else if (this.#live) {
      this.#cancelLive();
    }
  };

  connect() {
    if (!this.hasButtonTarget) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return;
    this.#form = this.element.closest("form") || this.element.querySelector("form");
    this.#form?.addEventListener("submit", this.#handleSubmit);
    this.inputTarget.addEventListener("input", this.#handleInput);
    this.buttonTarget.hidden = false;
  }

  disconnect() {
    this.#form?.removeEventListener("submit", this.#handleSubmit);
    this.#form = null;
    if (this.hasInputTarget) this.inputTarget.removeEventListener("input", this.#handleInput);
    // With the flag off the partial renders no button, so #cancel's style/busy
    // updates would hit a missing Stimulus target.
    if (this.hasButtonTarget) this.#cancel();
    clearTimeout(this.#noticeTimeout);
  }

  toggle() {
    if (this.#starting) {
      this.#cancelStarting();
    } else if (this.#live) {
      this.#stopLive();
    } else if (this.#recorder) {
      this.#finishRecording();
    } else {
      this.#start();
    }
  }

  // Startup is an owned, cancellable attempt: submit, disconnect, a second click, or
  // another composer taking over can all cancel it mid-flight without leaking the
  // microphone or a half-built live session.
  async #start() {
    if (activeController && activeController !== this) activeController.#cancel();
    activeController = this;

    const attempt = { cancelled: false, stream: null };
    this.#starting = attempt;

    try {
      try {
        attempt.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        this.#showNotice("Mo needs microphone access — allow it in your browser settings.");
        return;
      }
      if (!this.#isCurrentAttempt(attempt)) return;

      // Transfer microphone ownership to batch mode. When live is enabled the recorder
      // starts anyway, as a shadow: it captures from mic-grant, so if the live upgrade
      // fails, the batch recording still includes the opening speech.
      const stream = attempt.stream;
      attempt.stream = null;
      this.#starting = null;
      this.#startBatch(stream);

      if (this.streamUrlValue && this.#recorder) {
        await this.#tryUpgradeToLive(stream);
      }
    } finally {
      if (this.#starting === attempt) this.#starting = null;
      // No-op when ownership was transferred (the attempt's references are nulled on
      // transfer); otherwise releases whatever this attempt still holds.
      this.#cleanupAttempt(attempt);
      if (activeController === this && !this.#live && !this.#recorder && !this.#starting) {
        activeController = null;
      }
    }
  }

  #isCurrentAttempt(attempt) {
    return this.#starting === attempt && !attempt.cancelled;
  }

  #cancelStarting() {
    const attempt = this.#starting;
    if (!attempt) return;
    attempt.cancelled = true;
    this.#starting = null;
    this.#cleanupAttempt(attempt);
    if (activeController === this) activeController = null;
  }

  #cleanupAttempt(attempt) {
    attempt.stream?.getTracks().forEach((track) => track.stop());
    attempt.stream = null;
  }

  // --- Live engine: delegated to LiveDictationSession ---

  // Attempts to upgrade the running shadow batch recording to a live stream. On success
  // the shadow recording is discarded (its stream survives for the session; the buffered
  // startup PCM covers the opening speech). On any failure the shadow simply carries on
  // as the batch recording of record — nothing is lost.
  async #tryUpgradeToLive(stream) {
    const upgrade = { session: null };
    this.#upgrade = upgrade;
    const current = () => this.#upgrade === upgrade && this.#recorder;

    try {
      const session = new LiveDictationSession({
        stream,
        onTranscript: (from) => this.#writeLiveTranscript(from),
        onEnded: (from, reason) => this.#handleLiveEnded(from, reason),
        // Buffer full before the socket opened: abandon live rather than drop speech —
        // the shadow recording has everything.
        onOverflow: () => {
          if (this.#upgrade === upgrade) this.#abandonUpgrade();
        },
      });
      upgrade.session = session;

      if (!(await session.beginCapture())) return;
      if (!current()) {
        session.cancel();
        return;
      }

      let config;
      try {
        const response = await post(this.streamUrlValue);
        if (response.ok) config = await response.json;
      } catch {
        // shadow batch carries on
      }
      if (!config?.url || !current()) {
        session.cancel();
        return;
      }

      const started = await session.connect({ url: config.url, sampleRate: config.sample_rate });
      if (!started) return;
      if (!current()) {
        session.cancel();
        return;
      }

      // Promote: discard the shadow recording, handing its stream to the session.
      const recorder = this.#recorder;
      this.#recorder = null;
      recorder.preserveStream = true;
      session.takeStreamOwnership();
      clearTimeout(this.#maxTimer);
      if (recorder.state === "recording") recorder.stop();

      this.#upgrade = null;
      this.#live = session;
      this.#baseValue = this.#currentBase();
      // Transcripts may have arrived while the startup backlog was still flushing.
      if (session.text) this.#writeLiveTranscript(session);
      const remaining = Math.max(0, recorder.deadline - performance.now());
      this.#maxTimer = setTimeout(() => this.#stopLive(), remaining);
    } finally {
      if (this.#upgrade === upgrade) this.#upgrade = null;
    }
  }

  #abandonUpgrade() {
    const upgrade = this.#upgrade;
    if (!upgrade) return;
    this.#upgrade = null;
    upgrade.session?.cancel();
  }

  #writeLiveTranscript(session) {
    if (this.#live !== session) return;
    this.inputTarget.value = `${this.#baseValue}${session.text}`.replace(/\s+$/, "");
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  #stopLive() {
    const session = this.#live;
    if (!session || session.stopping) return;
    clearTimeout(this.#maxTimer);
    this.#setRecordingStyles(false);
    this.#setBusy(true);
    session.stop();
  }

  #handleLiveEnded(session, reason) {
    if (this.#live !== session) return;
    this.#live = null;
    clearTimeout(this.#maxTimer);
    this.#setRecordingStyles(false);
    this.#setBusy(false);
    if (activeController === this) activeController = null;
    if (reason === "finalized") {
      this.inputTarget.value = `${this.#baseValue}${session.text}`.replace(/\s+$/, "");
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
      this.#focusInput();
    } else {
      // Interruption: keep the displayed text as-is (nothing gets promoted as if the
      // transcript completed) and tell the user what happened.
      this.#showNotice("Live dictation dropped — some speech may be missing.");
    }
  }

  #cancelLive() {
    const session = this.#live;
    if (!session) return;
    this.#live = null;
    clearTimeout(this.#maxTimer);
    session.cancel();
    this.#setRecordingStyles(false);
    this.#setBusy(false);
    if (activeController === this) activeController = null;
  }

  #currentBase() {
    const existing = this.inputTarget.value.replace(/\s+$/, "");
    return existing ? `${existing} ` : "";
  }

  // --- Batch engine: MediaRecorder upload to the transcriptions endpoint ---

  #startBatch(stream) {
    let recorder;
    try {
      recorder = new MediaRecorder(stream, this.#recorderOptions());
    } catch {
      this.#abortStart(stream);
      return;
    }
    this.#chunks = [];
    this.#discarded = false;
    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) this.#chunks.push(event.data);
    };
    recorder.onstop = () => {
      // preserveStream: the recording was a shadow discarded in favor of a live session
      // that now owns the stream.
      if (!recorder.preserveStream) stream.getTracks().forEach((track) => track.stop());
      if (this.#recorder !== recorder) return;
      this.#recorder = null;
      const discarded = this.#discarded;
      const blob = new Blob(this.#chunks, { type: recorder.mimeType || "audio/webm" });
      this.#chunks = [];
      this.#setRecordingStyles(false);
      if (!discarded && blob.size > 0) this.#upload(blob);
    };

    this.#recorder = recorder;
    this.#setRecordingStyles(true);
    // The recording's hard deadline, which a live promotion inherits: startup audio is
    // buffered and streamed, so the cap runs from capture start, not from promotion.
    recorder.deadline = performance.now() + MAX_RECORDING_MS;
    this.#maxTimer = setTimeout(() => this.#finishRecording(), MAX_RECORDING_MS);
    try {
      recorder.start();
    } catch {
      this.#recorder = null;
      clearTimeout(this.#maxTimer);
      this.#setRecordingStyles(false);
      this.#abortStart(stream);
      return;
    }
    this.#focusInput();
  }

  #abortStart(stream) {
    stream.getTracks().forEach((track) => track.stop());
    if (activeController === this) activeController = null;
    this.#showNotice("Recording failed to start — try again.");
  }

  // stop() throws if the recorder already left the "recording" state (double-click,
  // or a submit racing the async stop event), so every stop is state-guarded.
  #finishRecording() {
    if (!this.#recorder) return;
    // Stopping the batch recording makes it the recording of record; a pending live
    // upgrade is moot.
    this.#abandonUpgrade();
    clearTimeout(this.#maxTimer);
    if (this.#recorder.state === "recording") this.#recorder.stop();
  }

  // Cancel drops everything: a pending startup attempt, live or batch, in-progress or in-flight.
  #cancel() {
    this.#cancelStarting();
    this.#abandonUpgrade();
    this.#cancelLive();
    this.#uploadToken = null;
    this.#setBusy(false);
    if (activeController === this) activeController = null;
    if (!this.#recorder) return;
    clearTimeout(this.#maxTimer);
    this.#discarded = true;
    if (this.#recorder.state === "recording") this.#recorder.stop();
    this.#setRecordingStyles(false);
  }

  async #upload(blob) {
    const token = {};
    this.#uploadToken = token;
    this.#setBusy(true);

    const extension = blob.type.includes("mp4") ? "mp4" : "webm";
    const formData = new FormData();
    formData.append("audio", blob, `dictation.${extension}`);

    let text = null;
    let errorMessage = "Transcription failed — try again.";
    try {
      const response = await post(this.urlValue, { body: formData });
      if (this.#uploadToken !== token) return;
      const data = await response.json.catch(() => null);
      if (response.ok) {
        text = data?.text ?? "";
      } else if (data?.error) {
        errorMessage = data.error;
      }
    } catch {
      // fall through to the generic error notice
    }
    if (this.#uploadToken !== token) return;

    this.#uploadToken = null;
    this.#setBusy(false);
    if (activeController === this) activeController = null;
    if (text) {
      this.#insertText(text);
    } else if (text === null) {
      this.#showNotice(errorMessage);
    }
    // An empty transcript (no intelligible speech) inserts nothing and shows no error.
  }

  #insertText(text) {
    const input = this.inputTarget;
    const existing = input.value.replace(/\s+$/, "");
    input.value = existing ? `${existing} ${text}` : text;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    this.#focusInput();
  }

  #recorderOptions() {
    const mimeType = MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported?.(type));
    // Pinned bitrate keeps 60s of speech well under the server's size cap.
    const options = { audioBitsPerSecond: 32_000 };
    if (mimeType) options.mimeType = mimeType;
    return options;
  }

  #setRecordingStyles(recording) {
    this.buttonTarget.setAttribute("aria-pressed", recording);
    if (recording) {
      this.buttonTarget.classList.add(...RECORDING_CLASSES);
    } else {
      this.buttonTarget.classList.remove(...RECORDING_CLASSES);
    }
  }

  #setBusy(busy) {
    this.buttonTarget.disabled = busy;
    if (this.hasMicIconTarget) this.micIconTarget.hidden = busy;
    if (this.hasBusyIconTarget) this.busyIconTarget.hidden = !busy;
  }

  #showNotice(message) {
    if (!this.hasNoticeTarget) return;
    this.noticeTarget.textContent = message;
    this.noticeTarget.hidden = false;
    clearTimeout(this.#noticeTimeout);
    this.#noticeTimeout = setTimeout(() => {
      this.noticeTarget.hidden = true;
    }, 5000);
  }

  #focusInput() {
    const input = this.inputTarget;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}
